import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { File as IconoArchivo, FileImage, FileText, FileVideo, FileAudio, Loader2, Upload, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { TIPOS_ARCHIVO, acceptDeArchivo, tamanoLegible } from '../../../lib/formularios';
import { mensajeDe } from './utilidades';
import { SinRespuesta } from './CamposTexto';

/*
 * Pregunta "Subir archivos". Cada archivo se sube EN CUANTO se elige (no al enviar): así un
 * archivo grande muestra su progreso, falla antes de que la persona termine el formulario y el
 * envío final solo manda los ids. El servidor borra a las 24 h las subidas que nadie envió.
 *
 * `onSubiendo(+1/-1)` avisa a la página para que no deje enviar con subidas a medias.
 */

function iconoDe(mime = '') {
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('video/')) return FileVideo;
  if (mime.startsWith('audio/')) return FileAudio;
  if (mime.includes('pdf') || mime.includes('word') || mime.startsWith('text/')) return FileText;
  return IconoArchivo;
}

export default function SubidaArchivos({
  item, valor, onCambio, soloLectura, token, deshabilitado, onSubiendo, aria, enlaceArchivo,
}) {
  const conf = item.archivo || {};
  const maximo = conf.max_archivos || 1;
  const maxMb = conf.max_mb || 10;
  const archivos = Array.isArray(valor) ? valor : [];
  const [enCurso, setEnCurso] = useState([]); // [{tmp, nombre, tamano, progreso}]
  const input = useRef(null);
  // La lista cambia mientras suben varios a la vez: siempre se parte de la última, no de la del render
  const ultimos = useRef(archivos);
  ultimos.current = archivos;

  if (soloLectura) {
    if (!archivos.length) return <SinRespuesta />;
    return (
      <ul className="fr-archivos">
        {archivos.map((a) => {
          const Icono = iconoDe(a.mime);
          const url = enlaceArchivo ? enlaceArchivo(a) : null;
          return (
            <li key={a.id} className="fr-archivo">
              <Icono className="fr-archivo-icono" aria-hidden="true" />
              {url ? (
                <a className="fr-archivo-nombre" href={url} target="_blank" rel="noopener noreferrer">{a.nombre || 'Archivo'}</a>
              ) : (
                <span className="fr-archivo-nombre">{a.nombre || 'Archivo'}</span>
              )}
              {a.tamano ? <span className="fr-archivo-tamano">{tamanoLegible(a.tamano)}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  const libres = maximo - archivos.length - enCurso.length;

  const subir = async (archivo) => {
    const tmp = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setEnCurso((l) => [...l, { tmp, nombre: archivo.name, tamano: archivo.size, progreso: 0 }]);
    onSubiendo?.(1);
    try {
      const fd = new FormData();
      fd.append('item_id', item.id);
      fd.append('archivo', archivo);
      const { data } = await api.post(`/forms/publico/${token}/archivos`, fd, {
        skipAuthRedirect: true,
        timeout: 0,
        onUploadProgress: (e) => {
          const progreso = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setEnCurso((l) => l.map((x) => (x.tmp === tmp ? { ...x, progreso } : x)));
        },
      });
      const nuevo = { id: data.id, nombre: data.nombre || archivo.name, tamano: data.tamano ?? archivo.size, mime: data.mime || archivo.type };
      const lista = [...ultimos.current, nuevo];
      ultimos.current = lista;
      onCambio(lista);
    } catch (err) {
      toast.error(mensajeDe(err, `No se pudo subir ${archivo.name}`));
    } finally {
      setEnCurso((l) => l.filter((x) => x.tmp !== tmp));
      onSubiendo?.(-1);
    }
  };

  const elegir = (lista) => {
    const elegidos = Array.from(lista || []);
    if (!elegidos.length) return;
    if (elegidos.length > libres) {
      toast.error(libres > 0 ? `Solo puedes agregar ${libres} archivo${libres === 1 ? '' : 's'} más` : 'Ya alcanzaste el máximo de archivos');
    }
    elegidos.slice(0, Math.max(libres, 0)).forEach((a) => {
      if (a.size > maxMb * 1024 * 1024) {
        toast.error(`${a.name} supera el tamaño máximo de ${maxMb} MB`);
        return;
      }
      subir(a);
    });
  };

  const quitar = (a) => {
    const lista = ultimos.current.filter((x) => x.id !== a.id);
    ultimos.current = lista;
    onCambio(lista.length ? lista : null);
    // Si no se puede borrar en el servidor no pasa nada: caduca sola a las 24 h
    api.delete(`/forms/publico/${token}/archivos/${a.id}`, { skipAuthRedirect: true }).catch(() => {});
  };

  const tipos = (conf.tipos || []).map((t) => TIPOS_ARCHIVO[t]?.nombre).filter(Boolean);

  return (
    <div className="fr-subida" role="group" {...aria}>
      {(archivos.length > 0 || enCurso.length > 0) && (
        <ul className="fr-archivos">
          {archivos.map((a) => {
            const Icono = iconoDe(a.mime);
            return (
              <li key={a.id} className="fr-archivo">
                <Icono className="fr-archivo-icono" aria-hidden="true" />
                <span className="fr-archivo-nombre">{a.nombre}</span>
                <span className="fr-archivo-tamano">{tamanoLegible(a.tamano)}</span>
                <button type="button" className="fr-archivo-quitar" onClick={() => quitar(a)} aria-label={`Quitar ${a.nombre}`}>
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
          {enCurso.map((a) => (
            <li key={a.tmp} className="fr-archivo is-subiendo">
              <Loader2 className="fr-archivo-icono od-spin" aria-hidden="true" />
              <span className="fr-archivo-nombre">{a.nombre}</span>
              <span className="fr-archivo-progreso" role="progressbar" aria-valuenow={a.progreso} aria-valuemin={0} aria-valuemax={100} aria-label={`Subiendo ${a.nombre}`}>
                <span style={{ width: `${a.progreso}%` }} />
              </span>
              <span className="fr-archivo-tamano">{a.progreso}%</span>
            </li>
          ))}
        </ul>
      )}
      {libres > 0 && (
        <button
          type="button"
          className="fm-boton-borde fr-subida-boton"
          disabled={deshabilitado}
          onClick={() => input.current?.click()}
        >
          <Upload className="h-4 w-4" /> Agregar archivo
        </button>
      )}
      <input
        ref={input}
        type="file"
        hidden
        multiple={maximo > 1}
        accept={acceptDeArchivo(conf.tipos) || undefined}
        onChange={(e) => {
          elegir(e.target.files);
          e.target.value = '';
        }}
      />
      <p className="fm-texto-2 fr-subida-nota">
        {deshabilitado
          ? 'La subida de archivos está desactivada en la vista previa.'
          : `Máximo ${maximo} archivo${maximo === 1 ? '' : 's'} de hasta ${maxMb >= 1000 ? `${maxMb / 1000} GB` : `${maxMb} MB`} cada uno${tipos.length ? ` · ${tipos.join(', ')}` : ''}.`}
      </p>
    </div>
  );
}
